import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Check, Loader2, AlertCircle } from "lucide-react";
import { formatPrice } from "@/lib/format";

const POLL_INTERVAL = 2000;
const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { clear } = useCart();
  const [status, setStatus] = useState("checking"); // checking | paid | error | timeout
  const [amount, setAmount] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    let timer;

    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setAmount(data.amount);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) {
            clear();
            cleared.current = true;
          }
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setStatus("error");
          return;
        }
      } catch {
        setStatus("error");
        return;
      }
      attempts += 1;
      if (attempts >= MAX_POLLS) {
        setStatus("timeout");
        return;
      }
      timer = setTimeout(poll, POLL_INTERVAL);
    };
    poll();
    return () => clearTimeout(timer);
  }, [params, clear]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative z-10">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
        className="max-w-lg w-full text-center"
        data-testid="payment-success-page"
      >
        {status === "checking" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto text-aak-primary animate-spin mb-8" strokeWidth={1.3} />
            <h1 className="font-serif text-4xl text-aak-fg mb-3">Confirming your order</h1>
            <p className="text-sm text-aak-mutedfg">One moment while we speak with the atelier…</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 mx-auto border border-aak-primary flex items-center justify-center mb-8">
              <Check className="w-7 h-7 text-aak-primary" strokeWidth={1.4} />
            </div>
            <p className="text-xs uppercase tracking-[0.3em] text-aak-primary mb-5">Order Confirmed</p>
            <h1 className="font-serif font-light text-5xl md:text-6xl tracking-tighter text-aak-fg mb-4">
              Thank you.
            </h1>
            <p className="text-sm text-aak-mutedfg mb-2">
              Your pieces are being prepared and pressed by hand.
            </p>
            {amount != null && (
              <p className="text-sm text-aak-fg mb-8">Total paid — {formatPrice(Number(amount))}</p>
            )}
            <button
              data-testid="back-home-btn"
              onClick={() => navigate("/")}
              className="bg-aak-fg text-aak-bg px-8 py-4 text-xs uppercase tracking-[0.25em] hover:bg-aak-primary transition-colors duration-500"
            >
              Return to AAK
            </button>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <AlertCircle className="w-12 h-12 mx-auto text-aak-primary mb-8" strokeWidth={1.3} />
            <h1 className="font-serif text-4xl text-aak-fg mb-3">
              {status === "timeout" ? "Still processing" : "Something went wrong"}
            </h1>
            <p className="text-sm text-aak-mutedfg mb-8">
              {status === "timeout"
                ? "Your payment is taking longer than usual. Check your email for confirmation."
                : "We couldn't confirm this payment. If you were charged, please contact us."}
            </p>
            <button
              onClick={() => navigate("/")}
              className="bg-aak-fg text-aak-bg px-8 py-4 text-xs uppercase tracking-[0.25em] hover:bg-aak-primary transition-colors duration-500"
            >
              Return to AAK
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}
